import { jsx as _jsx } from "react/jsx-runtime";
import { assertContext, createValueStorage } from '@bos/utils';
import { generateRandomCodeVerifier, generateRandomState } from 'oauth4webapi';
import { createContext, useContext, useEffect, useRef } from 'react';
import { generateAuthorizationUrl } from '../../helpers/buildAuthorizationUrl';
import { extractTokensFromResponse } from '../../helpers/extractTokensFromResponse';
import { getFullPathFromUrl } from '../../helpers/getFullPathFromUrl';
import { storeTokens } from '../../helpers/storeTokens';
import { useTokenManager } from '../../hooks/useTokenManager';
import { useAuthorizationServerInfoQuery } from '../../queries/useAuthorizationServerInfoQuery';
import { useProcessCodeRedirectCallbackMutation } from '../../queries/useProcessCodeRedirectCallbackMutation';
import { redirectSessionSchema, } from './redirectSessionSchema';
const LOGIN_RESPONSE_TYPE = 'code';
const LOGIN_SCOPES = ['openid', 'email'];
export const createOpenIdConnectContext = ({ tokenStorage, client, redirectCallbackUrl, }) => {
    const { serviceName } = tokenStorage;
    const oidcContext = createContext(null);
    const redirectSessionStorage = createValueStorage(`${serviceName}-redirectSession`, redirectSessionSchema, sessionStorage);
    const OpenIdConnectProvider = ({ children }) => {
        const { data: authorizationServer, isPending: isPendingAuthorizationServer, } = useAuthorizationServerInfoQuery(client.issuerUrl);
        const tokenManager = useTokenManager({
            tokenStorage,
            issuerUrl: client.issuerUrl,
            clientCredentials: {
                clientId: client.clientId,
                clientSecret: client.clientSecret,
            },
        });
        const authorizationEndpoint = authorizationServer?.authorization_endpoint;
        const isLoginRedirectAvailable = !!authorizationEndpoint;
        const isLoginRedirectLoading = isPendingAuthorizationServer;
        const redirectToLoginPage = async ({ extraParameters, }) => {
            if (!isLoginRedirectAvailable)
                return;
            const codeVerifier = generateRandomCodeVerifier();
            const state = generateRandomState();
            const authorizationUrl = await generateAuthorizationUrl({
                authorizationEndpoint,
                clientId: client.clientId,
                codeVerifier,
                state,
                redirectCallbackUrl,
                responseType: LOGIN_RESPONSE_TYPE,
                scopes: LOGIN_SCOPES,
                extraParameters,
            });
            const currentPath = getFullPathFromUrl(new URL(window.location.href));
            redirectSessionStorage.set({
                codeVerifier,
                state,
                pathBeforeRedirect: currentPath,
            });
            window.location.href = authorizationUrl;
        };
        const value = {
            ...tokenManager,
            isLoginRedirectAvailable,
            isLoginRedirectLoading,
            redirectToLoginPage,
        };
        return (_jsx(oidcContext.Provider, { value: value, children: children }));
    };
    const useOpenIdConnectContext = () => {
        const context = useContext(oidcContext);
        assertContext(context, `${serviceName}-openIdConnectContext`);
        return context;
    };
    const useOpenIdConnectCallbackHandler = ({ onSuccess, onError, }) => {
        const currentOidcContext = useOpenIdConnectContext();
        const callbackCalledRef = useRef(false);
        const { data: authorizationServer } = useAuthorizationServerInfoQuery(client.issuerUrl);
        const handleSessionNotFound = () => {
            onError(new Error('redirect session not found in storage'), undefined);
        };
        const processCodeRedirectCallbackMutation = useProcessCodeRedirectCallbackMutation({
            onSuccess: (result) => {
                const session = redirectSessionStorage.get();
                if (!session) {
                    handleSessionNotFound();
                    return;
                }
                const tokens = extractTokensFromResponse(result);
                storeTokens(tokenStorage, tokens);
                currentOidcContext.updateTokenState();
                onSuccess(session);
            },
            onError: (error) => {
                const session = redirectSessionStorage.get();
                onError(error, session);
            },
            onSettled: () => {
                redirectSessionStorage.remove();
            },
        });
        useEffect(() => {
            if (!authorizationServer || callbackCalledRef.current)
                return;
            callbackCalledRef.current = true;
            const currentUrl = new URL(window.location.href);
            const session = redirectSessionStorage.get();
            if (!session) {
                handleSessionNotFound();
                return;
            }
            const { codeVerifier, state } = session;
            processCodeRedirectCallbackMutation.mutate({
                authorizationServer,
                clientCredentials: client,
                urlWithCallbackResult: currentUrl,
                originalRedirectUrl: redirectCallbackUrl,
                codeVerifier,
                expectedState: state,
            });
        }, [authorizationServer]);
    };
    return {
        OpenIdConnectProvider,
        useOpenIdConnectContext,
        useOpenIdConnectCallbackHandler,
    };
};
